import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * What this deployment is configured to do.
 *
 * READ-ONLY, on purpose, and the empty half of the page is the interesting part.
 *
 * A platform settings page invites a form, and every candidate value failed one
 * of three tests. Default currency, timezone and country already carry a
 * `.default()` in the tenant create schema, so a platform default would be read
 * after the value was decided — a form that changes nothing. Default locale
 * would edit a column nothing reads. The step-up window and the rate limits are
 * read per request, and at 183ms to Ireland a per-request settings read is
 * either a slower request or a cache — and needing a cache is the tell that a
 * value does not belong in the database.
 *
 * What survives is nothing worth typing. But "is this deployment letting API
 * keys in?" had no answer short of an SSH session, and that is worth a page.
 */

interface Settings {
  deployment: {
    environment: string; log_level: string;
    api_keys_enabled: boolean; scheduler_enabled: boolean; node_db_attached: boolean;
  };
  sessions: {
    access_token_ttl_sec: number; refresh_token_ttl_sec: number;
    refresh_cookie_path: string; step_up_window_sec: number;
  };
  network: { trust_proxy_hops: number; cors_origins: string; shutdown_grace_ms: number };
  secrets_configured: {
    jwt_secret: boolean; encryption_key: boolean; supabase_service_role: boolean; resend_api_key: boolean;
  };
  reserved_slugs: string[];
}

function duration(sec: number): string {
  if (sec % 86400 === 0) return `${sec / 86400} days`;
  if (sec % 3600 === 0) return `${sec / 3600} hours`;
  if (sec % 60 === 0) return `${sec / 60} minutes`;
  return `${sec} seconds`;
}

/** A boolean that reads as a state, not a tick. */
function Flag({ on, onLabel, offLabel, danger }: {
  on: boolean; onLabel: string; offLabel: string; danger?: 'on' | 'off';
}) {
  const alarming = danger === (on ? 'on' : 'off');
  return (
    <span style={alarming ? { color: 'var(--arc-warn)', fontWeight: 550 } : undefined}>
      {on ? onLabel : offLabel}
    </span>
  );
}

export function SettingsPage() {
  const q = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => api.get<Settings>('/v1/platform/settings'),
  });

  if (q.error) return <ErrorBanner error={q.error} />;
  const s = q.data;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Platform settings</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            How this deployment is configured. Read-only — these come from the
            environment, and changing one is a deploy.
          </p>
        </div>
      </div>

      {q.isLoading || !s ? (
        <div className="arc-card"><TableSkeleton rows={4} cols={2} /></div>
      ) : (
        <>
          <div className="arc-card" style={{ marginBottom: 14 }}>
            <div className="arc-card-head"><h2 className="arc-h2">This deployment</h2></div>
            <div className="arc-card-body">
              <dl className="arc-kv">
                <dt>Environment</dt><dd className="arc-mono">{s.deployment.environment}</dd>
                <dt>Log level</dt><dd className="arc-mono">{s.deployment.log_level}</dd>
                {/*
                  The second door into the platform. Called out because "are API
                  keys on?" is a question with a security answer that nobody
                  could get at without an SSH session.
                */}
                <dt>API keys</dt>
                <dd>
                  <Flag on={s.deployment.api_keys_enabled} danger="on"
                    onLabel="enabled — a second way in" offLabel="disabled" />
                </dd>
                <dt>Scheduled jobs</dt>
                <dd>
                  <Flag on={s.deployment.scheduler_enabled} danger="off"
                    onLabel="running on this instance"
                    offLabel="not running here — nothing maintains partitions" />
                </dd>
                <dt>Node database</dt>
                <dd>
                  <Flag on={s.deployment.node_db_attached}
                    onLabel="attached (presence is live)"
                    offLabel="detached — presence will be blank" />
                </dd>
              </dl>
            </div>
          </div>

          <div className="arc-card" style={{ marginBottom: 14 }}>
            <div className="arc-card-head"><h2 className="arc-h2">Sessions</h2></div>
            <div className="arc-card-body">
              <dl className="arc-kv">
                <dt>Access token</dt><dd>{duration(s.sessions.access_token_ttl_sec)}</dd>
                <dt>Refresh token</dt><dd>{duration(s.sessions.refresh_token_ttl_sec)}</dd>
                <dt>Step-up window</dt>
                <dd>
                  {duration(s.sessions.step_up_window_sec)}
                  <span className="arc-subtle">
                    {' '}— how long a password re-check lasts before a balance
                    adjustment or a credential reveal asks again
                  </span>
                </dd>
                <dt>Cookie path</dt><dd className="arc-mono">{s.sessions.refresh_cookie_path}</dd>
              </dl>
            </div>
          </div>

          <div className="arc-card" style={{ marginBottom: 14 }}>
            <div className="arc-card-head"><h2 className="arc-h2">Network</h2></div>
            <div className="arc-card-body">
              <dl className="arc-kv">
                <dt>Trusted proxy hops</dt>
                <dd className="arc-num">
                  {s.network.trust_proxy_hops}
                  <span className="arc-subtle">
                    {' '}— too high and a client can forge its own address to
                    dodge rate limits; too low and everyone shares one bucket
                  </span>
                </dd>
                <dt>CORS origins</dt><dd className="arc-mono">{s.network.cors_origins}</dd>
                <dt>Shutdown grace</dt><dd>{s.network.shutdown_grace_ms} ms</dd>
              </dl>
            </div>
          </div>

          <div className="arc-card" style={{ marginBottom: 14 }}>
            <div className="arc-card-head">
              <h2 className="arc-h2">Secrets</h2>
              <span className="arc-subtle" style={{ fontSize: 12 }}>
                whether one is set, never what it is
              </span>
            </div>
            <div className="arc-card-body">
              <dl className="arc-kv">
                <dt>Token signing key</dt>
                <dd><Flag on={s.secrets_configured.jwt_secret} danger="off"
                  onLabel="configured" offLabel="MISSING" /></dd>
                <dt>Encryption key</dt>
                <dd>
                  <Flag on={s.secrets_configured.encryption_key} danger="off"
                    onLabel="configured" offLabel="MISSING" />
                  <span className="arc-subtle">
                    {' '}— seals carrier credentials and SIP passwords. There is
                    no re-encryption path, so rotating it makes every sealed
                    value permanently unreadable.
                  </span>
                </dd>
                <dt>Supabase service role</dt>
                <dd><Flag on={s.secrets_configured.supabase_service_role}
                  onLabel="configured" offLabel="not set" /></dd>
                <dt>Resend API key</dt>
                <dd>
                  <Flag on={s.secrets_configured.resend_api_key}
                    onLabel="configured" offLabel="not set" />
                  <span className="arc-subtle">
                    {' '}— sends receipts and low-balance warnings; without it they wait
                  </span>
                </dd>
              </dl>
            </div>
          </div>

          <div className="arc-card">
            <div className="arc-card-head">
              <h2 className="arc-h2">Reserved names</h2>
              <span className="arc-subtle" style={{ fontSize: 12 }}>
                {s.reserved_slugs.length} refused as a slug
              </span>
            </div>
            <div className="arc-card-body">
              <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
                A slug becomes a hostname label, so these would collide with the
                platform&rsquo;s own. Refused for both customer accounts and
                resellers, from one list.
              </p>
              <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {s.reserved_slugs.map((r) => (
                  <span key={r} className="arc-mono arc-subtle" style={{ fontSize: 12 }}>{r}</span>
                ))}
              </div>
            </div>
          </div>

          <p className="arc-hint" style={{ marginTop: 14 }}>
            <Icon name="info" size={13} /> Nothing here is editable from a
            browser. Rotating the encryption key would make every sealed
            credential unreadable; changing CORS or the proxy hop count locks you
            out of the page you would use to change it back. These belong to the
            deploy.
          </p>
        </>
      )}
    </>
  );
}
